import { Box, styled, Theme, useMediaQuery } from '@mui/material';
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CardFlightLayer } from './card-flight-layer';
import { CARD_HEIGHT, CARD_WIDTH, DeckCard } from '../deck-card';
import { Card, InPlayRoomWsResponse } from '../../../types';
import { ErrorLayout } from '../../../../../shared/components';

import { useSelector } from 'react-redux';
import { Store } from '../../../../../redux';
import { useThrowCard } from '../../context';
import { CardsRequestNotifier } from '../cards-request';

import { GroupChat } from '../group-chat';
import { usePlayground } from './hooks';
import {
  BottomLeftBox,
  InPlayCardsWrapper,
  PlaygroundWrapper,
  Thoka,
} from './styled';
import { RequestCardsButton } from './request-cards';
import { InRoomPlayerList, PlayerPosition } from './in-room-player-list';
import { soundEffect } from '../../../../../shared/lib';
import { dividePlayers, otherPlayerCardAnimation } from './utils';
import { cloneDeep } from 'lodash';
import { SendMessage } from '@/screens/authenticated/use-websocket';
import { useTableOptions } from '../../table-options';

export interface PlaygroundProps {
  inPlayWS: InPlayRoomWsResponse;
  sendMessage: SendMessage;
  isVerticalMode?: boolean;
}

export const Playground: FC<PropsWithChildren<PlaygroundProps>> = ({
  inPlayWS,
  sendMessage,
  isVerticalMode = false,
}) => {
  const inPlayCards = useMemo(
    () => inPlayWS?.gameInfo?.inPlayCards || [],
    [inPlayWS?.gameInfo?.inPlayCards]
  );

  const noOfPlayers = useMemo(
    () => inPlayWS?.gameInfo?.room?.numberOfPlayers || null,
    [inPlayWS?.gameInfo?.room?.numberOfPlayers]
  );

  const userID = useSelector((state: Store) => state.userReducer.id);
  const { isGuestTable } = useTableOptions();

  // checking if there are other users in the room or there are all bots
  const isOtherUsers = useMemo(() => {
    const otherUser = inPlayWS?.players?.filter(
      player => player.id !== userID && player.userType !== 'bot'
    );

    return !!otherUser?.length;
  }, [inPlayWS?.players, userID]);

  const sortedPlayers = useMemo(() => {
    if (!inPlayWS.players.length) return [];
    const sorted = cloneDeep(inPlayWS.players).sort(
      (a, b) => a.entryNo - b.entryNo
    );
    // For a 1-vs-1 match, drop the local player from the around-the-table
    // avatars so the only opponent gets the centered top slot facing the
    // local player (whose deck is already anchored to the bottom). For 3+
    // player games we keep the original layout where every player appears
    // around the table (including the local user — their seat completes
    // the geometry).
    if (sorted.length === 2 && userID) {
      return sorted.filter(p => p.id !== userID);
    }
    return sorted;
  }, [inPlayWS.players, userID]);

  const [one, two, three] = useMemo(
    () => dividePlayers(sortedPlayers),
    [sortedPlayers]
  );

  const [playerPos, setPlayerPosition] = useState<PlayerPosition[]>([]);

  const lastNumberOfCards = useRef(0);
  const [lastCard, setLastCard] = useState<Card | null>(null);
  const [inPlayCardsClone, setInPlayCardsClone] = useState<Card[]>([]);
  const isMd = useMediaQuery<Theme>(theme => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (!inPlayCards.length) {
      lastNumberOfCards.current = 0;
      setLastCard(null);
      setInPlayCardsClone([]);
      return;
    }
    if (inPlayCards.length !== lastNumberOfCards.current) {
      lastNumberOfCards.current = inPlayCards.length;
      setLastCard(inPlayCards[inPlayCards.length - 1]);
    }
  }, [inPlayCards]);

  const findPlacement = () => {
    const player = playerPos.find(player => player.id === lastCard?.userID);
    if (player?.isHorizontal) {
      return undefined;
    }

    if (two.some(player => player.id === lastCard?.userID)) {
      return 'left';
    }

    return 'right';
  };

  useEffect(() => {
    if (lastCard) {
      const timer = window.setTimeout(() => {
        setLastCard(null);
        setInPlayCardsClone(inPlayCards);
      }, 600);
      return () => window.clearTimeout(timer);
    }
  }, [lastCard, inPlayCards]);

  if (!noOfPlayers) {
    return (
      <ErrorLayout
        outerBoxProps={{
          height: {
            sm: `calc(100vh - ${CARD_HEIGHT.sm})`,
            md: `calc(100vh - ${CARD_HEIGHT.md})`,
          },
        }}
      />
    );
  }

  return (
    <PlaygroundWrapper
      className={`bh-playground container mx-auto ${isVerticalMode ? 'mt-10' : 'mt-[-32px]'}`}
    >
      {lastCard && lastCard.userID !== userID && (
        <CardFlightLayer>
          <DeckCard
            card={lastCard}
            motionDivProps={otherPlayerCardAnimation(
              lastCard.userID,
              playerPos.filter(player => player.id !== userID),
              isMd,
              findPlacement()
            )}
          />
        </CardFlightLayer>
      )}
      {inPlayWS?.gameInfo?.room?.roomID.length &&
        isOtherUsers &&
        !isGuestTable && (
          <Box
            sx={theme => ({
              position: 'absolute',
              // When the keyboard is closed, sit above the cards / controls
              // (120px portrait, 20px landscape) plus the home-indicator
              // safe area. When the keyboard is open we lift to exactly
              // var(--kb-inset) so the trigger — and therefore the popover
              // that anchors above it — sits flush on top of the keyboard.
              // The `max(...)` picks whichever is larger, so the trigger
              // is never both above the cards AND clear of the keyboard
              // simultaneously (which would leave a dead band between the
              // input and the keyboard top edge — the empty space the user
              // reported).
              bottom: `max(calc(${isVerticalMode ? 120 : 20}px + env(safe-area-inset-bottom)), var(--kb-inset, 0px))`,
              transition: 'bottom var(--kb-transition, 0ms linear)',
              right: theme.spacing(2),
              zIndex: 2300,
            })}
          >
            <GroupChat
              roomID={inPlayWS?.gameInfo?.room?.roomID}
              isVerticalMode={isVerticalMode}
            />
          </Box>
        )}
      {inPlayWS?.showRequestCardsOption && (
        <BottomLeftBox isVerticalMode={isVerticalMode}>
          <RequestCardsButton {...{ sendMessage }} />
        </BottomLeftBox>
      )}
      {inPlayWS?.gameInfo?.room?.cardsRequestFrom?.length && (
        <BottomLeftBox isVerticalMode={isVerticalMode}>
          <CardsRequestNotifier
            requestUsers={inPlayWS?.gameInfo?.room?.cardsRequestFrom}
            sendMessage={sendMessage}
          />
        </BottomLeftBox>
      )}
      <div className="flex flex-col justify-center w-full">
        <div
          className={`flex items-center justify-center sm:py-1 lg:py-3 ${isVerticalMode ? 'pb-4' : ''}`}
        >
          <InRoomPlayerList
            setPlayerPosition={setPlayerPosition}
            nextTurn={inPlayWS.gameInfo.room?.nextTurn ?? ''}
            noOfPlayers={noOfPlayers}
            players={one}
            isHorizontal
          />
        </div>
        <div className="bh-table-seating">
          <InRoomPlayerList
            setPlayerPosition={setPlayerPosition}
            nextTurn={inPlayWS.gameInfo.room?.nextTurn ?? ''}
            noOfPlayers={noOfPlayers}
            players={two}
          />
          <RoundedTable className="bh-real-table">
            <span className="bh-table-stamp" aria-hidden="true">
              BHABI<span>♠ &nbsp; ♥ &nbsp; ♣ &nbsp; ♦</span>
            </span>
            <InPlayCardsWithLayout
              cards={
                lastCard?.userID !== userID ? inPlayCardsClone : inPlayCards
              }
              concludedCards={inPlayWS?.concludedCards}
            />
          </RoundedTable>
          <InRoomPlayerList
            setPlayerPosition={setPlayerPosition}
            nextTurn={inPlayWS.gameInfo.room?.nextTurn ?? ''}
            noOfPlayers={noOfPlayers}
            players={three}
          />
        </div>
      </div>
    </PlaygroundWrapper>
  );
};

export interface InPlayCardsWithLayoutProps {
  cards: Card[];
  concludedCards: number;
}

export const InPlayCardsWithLayout: FC<InPlayCardsWithLayoutProps> = ({
  cards,
}) => {
  const { gridWidth, setGridWidth } = useThrowCard();
  const { turnRes } = usePlayground(setGridWidth);
  const md = useMediaQuery<Theme>(theme => theme.breakpoints.down('lg'));

  useEffect(() => {
    setGridWidth(md ? CARD_WIDTH.md / (cards.length + 1) : CARD_WIDTH.sm * 1.6);
  }, [md, cards.length, setGridWidth]);

  const lastCardsLength = useRef(0);

  useEffect(() => {
    if (!cards.length) {
      lastCardsLength.current = 0;
      return;
    }

    if (lastCardsLength.current < cards.length && !turnRes?.thoka) {
      lastCardsLength.current = cards.length;
      soundEffect.playSound(soundEffect.punch);
    }
  }, [cards.length, turnRes?.thoka]);

  return (
    <InPlayCardsWrapper
      px={1}
      numberOfPlayers={cards.length + 1}
      gridWidth={gridWidth}
    >
      {turnRes?.thoka && <Thoka />}
      <Box className="inner_box bh-table-cards">
        {/* <ConcludedCardsDeck {...{ concludedCards }} /> */}
        {cards.map((card, i) => (
          <DeckCard
            card={card}
            key={`${card.suite}-${card.value}`}
            motionDivProps={{
              initial: { opacity: 0, y: -28, scale: 1.1, rotate: 0 },
              animate: {
                opacity: 1,
                y: (i % 2) * 8,
                scale: 1,
                rotate: [-7, 5, -3, 8, -5, 3, -8, 4, 0][i % 9],
              },
              transition: { type: 'spring', stiffness: 240, damping: 23 },
            }}
          />
        ))}
      </Box>
    </InPlayCardsWrapper>
  );
};

const RoundedTable = styled(Box)(({ theme }) => ({
  position: 'relative',
  isolation: 'isolate',
  borderRadius: '46% / 28%',
  border: '12px solid #62412b',
  backgroundColor: '#14523b',
  backgroundImage:
    'radial-gradient(ellipse at 42% 26%, rgba(86, 153, 100, .42), transparent 70%), repeating-linear-gradient(35deg, rgba(255,255,255,.025) 0 1px, transparent 1px 4px)',
  boxShadow:
    '0 4px 0 #a97a48, 0 10px 0 #352014, 0 17px 0 #1b100b, 0 27px 35px rgba(0,0,0,.6), inset 0 0 0 3px #baa16a, inset 0 0 0 7px #0a3024, inset 0 10px 30px rgba(0,0,0,.6)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 13,
    borderRadius: 'inherit',
    border: '1px dashed rgba(226,207,153,.34)',
    pointerEvents: 'none',
    zIndex: -1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: -11,
    borderRadius: 'inherit',
    borderTop: '3px solid rgba(255,216,150,.28)',
    borderBottom: '3px solid #24130c',
    pointerEvents: 'none',
  },
  display: 'flex',
  justifyContent: 'center',
  height: '42vh',
  minHeight: 210,
  [theme.breakpoints.down('lg')]: {
    height: '42vh',
    minHeight: 210,
  },
}));
